/**
 * Care-line label for the single-order header chip, sourced only from Postgres auth-on-file
 * J-codes (`entries` / `auth_entries` → `j_codes[].initiation_continuation_status`).
 *
 * No `flowsheet_regimen_details` / EMR regimen_tag dependency.
 *
 * @module regimenCareHeaderChip
 */
import { AuthOnFile } from "../../../../../data-model/medicalPaOrdersModel";
import { JCodeStatusValue } from "../../../../../enums/jCodeIssueLabel";
import { getAllJCodesFromAuthOnFile } from "../../../utils/existingDocsToLs";

/**
 * Derives `Continuation of care` vs `Initial line` for {@link getBadgeDetailsAndIconColorForCareTag}
 * from every J-code’s `initiation_continuation_status`.
 *
 * @param authOnFile - Auth-on-file payload with `entries` or `auth_entries`
 * @returns Canonical tag string, or empty when no J-codes or no status set
 */
export function resolveRegimenCareTagForHeader(
  authOnFile?: AuthOnFile | null,
): string {
  if (!authOnFile) {
    return "";
  }
  const jCodes = getAllJCodesFromAuthOnFile(authOnFile);
  if (!jCodes?.length) {
    return "";
  }
  const normalized = jCodes
    .map((j) => j?.initiation_continuation_status)
    .filter((s): s is string => s != null && String(s).trim() !== "")
    .map((s) => String(s).trim().toLowerCase());
  if (normalized.length === 0) {
    return "";
  }
  const allContinuation = normalized.every(
    (s) => s === JCodeStatusValue.CONTINUATION,
  );
  if (allContinuation) {
    return "Continuation of care";
  }
  return "Initial line";
}
